import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Layout, Menu, Input, Drawer, Image, Flex } from 'antd';
import * as AntIcons from '@ant-design/icons';
import useScreenSize from '../../utils/useScreenSize';
import eCoachVMSApi from '../../Api';
import './sidebar.css';
import LogoMain from '../../images/Logo.png';
import LogoIcon from '../../images/logo-icon.png';

const { Sider } = Layout;
const { Search } = Input;

const Sidebar = ({ collapsed, open, onClose }) => {
  const { baseURL } = eCoachVMSApi();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useScreenSize();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);

  // Function to render icons - handles both special cases and dynamic icons
  const renderIcon = (iconName) => {
    if (!iconName) return null;
    
    // Define special icon mappings
    const specialIcons = {
      'anticon-home': <AntIcons.HomeOutlined />,
      'anticon-calendar': <AntIcons.CalendarOutlined />,
      'anticon-schedule': <AntIcons.ScheduleOutlined />,
      'anticon-exclamation-circle': <AntIcons.ExclamationCircleOutlined />,
      'anticon-file-text': <AntIcons.FileTextOutlined />,
      'anticon-file-done': <AntIcons.FileDoneOutlined />,
      'anticon-tool': <AntIcons.ToolOutlined />,
      'anticon-toolbox': <AntIcons.AppstoreOutlined />,
      'anticon-appstore': <AntIcons.AppstoreOutlined />,
      'anticon-setting': <AntIcons.SettingOutlined />
    };

    // If it's a special icon, return it
    if (specialIcons[iconName]) {
      return specialIcons[iconName];
    }

    // Try to render it as a dynamic icon
    const IconComponent = AntIcons[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  const buildMenuTree = (items) => {
    const itemMap = new Map();
    const menuTree = [];

    // Map all items by their IDs
    items.forEach(item => {
      itemMap.set(item.id, { ...item, children: [] });
    });

    // Build the tree structure
    items.forEach(item => {
      if (item.parent_id === null || item.parent_id === 0) {
        menuTree.push(itemMap.get(item.id));
      } else {
        const parent = itemMap.get(item.parent_id);
        if (parent) {
          parent.children.push(itemMap.get(item.id));
        }
      }
    });

    // Sort menu items by sort_order if available
    const sortItems = (items) => {
      items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          sortItems(item.children);
        }
      });
    };

    sortItems(menuTree);
    return menuTree;
  };

  // Fetch menu data from API
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await baseURL.get('/getMenu');
        const data = response.data;
        const menuTree = buildMenuTree(data);
        setMenuItems(menuTree);
        const openSubMenus = getOpenKeysFromPath(location.pathname, menuTree);
        setOpenKeys(openSubMenus);
      } catch (error) {
        console.error('Error fetching menu:', error);
      }
    };
    fetchMenu();
    // eslint-disable-next-line
  }, []);

  // Get open submenu keys based on current path
  const getOpenKeysFromPath = (path, menuTree) => {
    const openKeys = [];
    const findOpenSubMenu = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          item.children.forEach(child => {
            if (child.react_path === path) {
              openKeys.push(item.react_path);
            }
          });
          findOpenSubMenu(item.children);
        }
      });
    };

    findOpenSubMenu(menuTree);
    return openKeys;
  };

  const handleMenuClick = ({ key }) => {
    navigate(key);
    if ((isMobile || isTablet) && onClose) {
      onClose();
    }
  };

  // Recursively render menu items
  const renderMenuItems = (items) => {
    return items.map(item => {
      if (!item.is_active) return null;

      const isActive = location.pathname === item.react_path;
      const isParentActive = item.children && 
        item.children.some(child => location.pathname === child.react_path);
      const menuItemClass = isActive || isParentActive ? 'menu-item-active' : '';

      if (item.children && item.children.length > 0) {
        return (
          <Menu.SubMenu
            key={item.react_path}
            title={item.name}
            icon={renderIcon(item.icon)}
            className={menuItemClass}
          >
            {renderMenuItems(item.children)}
          </Menu.SubMenu>
        );
      }

      return (
        <Menu.Item
          key={item.react_path}
          icon={renderIcon(item.icon)}
          className={menuItemClass}
        >
          {item.name}
        </Menu.Item>
      );
    }).filter(Boolean); // Filter out null items
  };

  // Render desktop sidebar
  const renderDesktopSidebar = () => (
    <Sider 
      width={220} 
      trigger={null} 
      collapsible 
      collapsed={collapsed} 
      style={{ backgroundColor: '#f8fafb' }}
    >
      <Flex 
        align="center" 
        justify="center" 
        style={{ 
          padding: 5, 
          backgroundColor: '#3382b0', 
          textAlign: 'center', 
          minHeight: '51px' 
        }}
      >
        {!collapsed ? (
          <Link to="/">
            <Image src={LogoMain} preview={false} style={{ height: '40px', objectFit: 'contain' }} />
          </Link>
        ) : (
          <Link to="/">
            <Image src={LogoIcon} preview={false} style={{ height: '32px', objectFit: 'contain' }} />
          </Link>
        )}
      </Flex>

      {!collapsed && (
        <div className='sidemenu' style={{ 
          padding: '15px 12px', 
          marginBottom: 2, 
          borderBottom: '1px solid #dbdbdb' 
        }}>
          <h3 className="name">Hello, Torben</h3>
          <Search placeholder="Search..." />
        </div>
      )}

      <Menu
        className="sidebar-menus"
        mode="inline"
        onClick={handleMenuClick}
        style={{ backgroundColor: 'transparent', border: '0 none' }}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
      >
        {renderMenuItems(menuItems)}
      </Menu>
    </Sider>
  );

  // Render mobile drawer
  const renderMobileDrawer = () => (
    <Drawer 
      placement="left" 
      width={280} 
      onClose={onClose} 
      open={open} 
      className="mobile-drawer-sidebar"
    >
      <Flex 
        align="center" 
        justify="center" 
        style={{ 
          padding: 5, 
          backgroundColor: '#3382b0', 
          textAlign: 'center', 
          minHeight: '51px' 
        }}
      >
        <Image src={LogoMain} preview={false} style={{ height: '40px' }} />
      </Flex>

      <div style={{ 
        padding: '15px 12px', 
        marginBottom: 2, 
        borderBottom: '1px solid #dbdbdb' 
      }}>
        <h3 className="name">Hello, Torben</h3>
        <Search placeholder="Search..." />
      </div>

      <Menu
        className="sidebar-menus"
        mode="inline"
        onClick={handleMenuClick}
        style={{ backgroundColor: 'transparent', border: '0 none' }}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
      >
        {renderMenuItems(menuItems)}
      </Menu>
    </Drawer>
  );

  return (
    <>
      {isMobile || isTablet ? renderMobileDrawer() : renderDesktopSidebar()}
    </>
  );
};

export default Sidebar;